import React, { useState, useEffect } from 'react';
import { Link } from '@curi/react';
import __isEmpty from 'lodash/isEmpty';
import OutsideClickHandler from 'react-outside-click-handler';

import './Header.css';
import Collapsible from '../Collapsible';
import { useExperimentContext } from '../../../experiments/context';
import { sendAnalyticsEvent } from '../../../services/analytics';
import { getBannerData } from './bannerData';
import { botClassName } from '../../../utils/e2eBotUtils';
import { BeginGlobalHeader } from '@learnwithhomer/begin-component-library';
import { useGetReferrerParams, useSubscribeToBeginHeaderEvents } from './hooks';
import { RenderUnleashVariant } from '../../../utils/experiments';
import { generateVariantData, getEnabledVariant } from '../../../experiments/utils';
import {
  CTA_LABEL_FOR_DIFFERENT_TRIAL_LENGTHS,
  EXPERIMENT__DIFFERENT_PLAN_TRIALS,
  EXPERIMENT__UPDATED_TRIAL_LABELS
} from '../../../experiments/constants';
import { gtmCtaTapEvent } from '../../../services/google-tag-manager';
import { isFlagEnabled, useCodeSparkFlags } from '../../../featureFlags';
import { FEATURE_FLAG__BEGIN_15 } from '../../../constants/featureflags';

const imgURL = process.env.REACT_APP_CLOUDFRONT_URL;

function BannerLink({ onClick, children, ...props }) {
  const handleCtaClick = e => {
    e.preventDefault();
    onClick(e, 'marketing_banner');
  }

  return (
    <span className={`marketing-banner-cta ${botClassName('banner-cta')}`}>
      <a
        href={process.env.REACT_APP_SIGNUP_BASE_URL}
        onClick={handleCtaClick}
        {...props}
      >
        {children}
      </a>
    </span>
  );
}

function TopBanner({ onCtaClick, bannerProps }) {
  if (__isEmpty(bannerProps)) {
    return <div className="header-top-no-banner"></div>
  }
  const {
    bgColor,
    textColor,
    bannerTextDesktop,
    bannerCtaText,
    bannerTextMobile,
  } = bannerProps;

  return (
    <div
      className={`header-top-banner marketing-banner ${botClassName(
        'header-banner'
      )}`}
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div
        className={`marketing-banner-desktop ${botClassName('banner-desktop')}`}
      >
        <span
          className={`marketing-banner-text ${botClassName('banner-text')}`}
        >
          {bannerTextDesktop}
        </span>

        <BannerLink onClick={onCtaClick} style={{ color: textColor }}>
          {bannerCtaText.toUpperCase()}
        </BannerLink>
      </div>

      <div
        className={`marketing-banner-mobile ${botClassName('banner-mobile')}`}
      >
        <BannerLink onClick={onCtaClick} style={{ color: textColor }}>
          {bannerTextMobile}
        </BannerLink>
      </div>
    </div>
  );
}

const getCtaLabel = (planLabels, variant) => {
  const { trialLabel, trialQuantity, trialInterval } = planLabels;

  const ctaLabelToExperimentMapping = {
    updatedTrialLabels: trialLabel,
    differentPlanTrials: CTA_LABEL_FOR_DIFFERENT_TRIAL_LENGTHS,
    base: `${trialQuantity} ${trialInterval} FREE Trial`,
  };

  return generateVariantData(ctaLabelToExperimentMapping)(variant);
};

function useHeaderKey() {
  const { experiments } = useExperimentContext();
  // Some experiments and feature flags may change the header.
  // We could implement what homer has, too
  const enabledLabelExperiment = getEnabledVariant(experiments, [
    EXPERIMENT__DIFFERENT_PLAN_TRIALS,
    EXPERIMENT__UPDATED_TRIAL_LABELS,
  ]);
  const headerFeatureFlags = [FEATURE_FLAG__BEGIN_15];
  const flags = useCodeSparkFlags(headerFeatureFlags);
  const headerFlagStatuses = flags.filter(isFlagEnabled);
  // Pick the first feature with a header, we never show multiple marketing banners
  const enabledHeader = headerFlagStatuses[0];

  if (headerFlagStatuses.length > 1) {
    console.warn('[UNLEASH]: There are multiple feature flags attempting to set the marketing banner.');
  }

  if (enabledHeader) {
    return enabledHeader.name;
  }

  return enabledLabelExperiment;
}

export default function Header(props) {
  const [openParents, setOpenParents] = useState(false);
  const [openEducators, setOpenEducators] = useState(false);
  const [openAbout, setOpenAbout] = useState(false);
  const [openMobileMenu, setOpenMobileMenu] = useState(false);

  const { experiments } = useExperimentContext();
  const { passPlanTagToAccounts } = experiments;
  const enabledLabelExperiment = getEnabledVariant(experiments, [
    EXPERIMENT__DIFFERENT_PLAN_TRIALS,
    EXPERIMENT__UPDATED_TRIAL_LABELS,
  ]);

  const bannerKey = useHeaderKey();
  const bannerProps = getBannerData(props.planLabels, bannerKey);
  const ctaLabel = getCtaLabel(props.planLabels, enabledLabelExperiment);
  const referrerParams = useGetReferrerParams();

  useSubscribeToBeginHeaderEvents();

  // close any menus if we transition to another page
  useEffect(() => {
    setOpenParents(false);
    setOpenEducators(false);
    setOpenAbout(false);
    setOpenMobileMenu(false);
  }, [props.location.pathname]);

  const toggleParents = () => {
    setOpenParents(!openParents);
  };

  const toggleEducators = () => {
    setOpenEducators(!openEducators);
  };

  const toggleAbout = () => {
    setOpenAbout(!openAbout);
  };

  const goToAccounts = (e, cta_id) => {
    const { planTag, goToLink, location: { query } } = props;
    const extraURLParams = {};
    if (passPlanTagToAccounts) {
      extraURLParams.activePlanTag = planTag;
    }
    const extraQueryParams = new URLSearchParams(extraURLParams).toString();

    gtmCtaTapEvent({
      pageName: 'home_page',
      location_on_page: cta_id,
    });
    sendAnalyticsEvent('web_cta_tapped', {
      'app': 'web-site',
      'location_on_page': cta_id,
      'screen_name': 'home_page',
    }, true);

    const queryParams = [];

    // need to check if there are any url params and then make sure those get passed through in the links
    if (!__isEmpty(query)) {
      queryParams.push(query);
    }

    if (extraQueryParams) {
      queryParams.push(extraQueryParams)
    }

    let accountsLink = process.env.REACT_APP_SIGNUP_BASE_URL;
    if (queryParams.length) {
      accountsLink += `?${queryParams.join('&')}`
    }

    goToLink(
      e,
      accountsLink
    );
  };

  if (props.location.pathname === '/girl-scouts') {
    return (
      <div className={`global-header-wrapper full-width ${props.isScrolling ? 'scrolling' : 'scrolling'}`}>
        <div className="header-top-container">
          <div className="logo">
            <a href={`${process.env.REACT_APP_CODESPARK_BASE_URL}/girl-scouts`}>
              <img src={`${imgURL}/global/girl-scouts/codespark_x_girlscouts_v2.png`} alt="codeSpark" />
            </a>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="header-wrapper">
        <div
          className={`
                        global-header-wrapper full-width main-header-wrapper
                        ${props.isScrolling ? 'scrolling' : ''}
                        ${props.isLanding ? 'landing-header-wrapper' : ''}
                        ${!__isEmpty(bannerProps) ? 'hasBanner' : ''}
                        ${props.location.pathname === '/privacy' ? 'not-fixed' : ''}`}
        >
          <RenderUnleashVariant featureId="codeSparkBeginHeader" variantName="showHeader">
            <BeginGlobalHeader activeBrand="codespark" urlParams={referrerParams} />
          </RenderUnleashVariant>

          <TopBanner
            onCtaClick={goToAccounts}
            bannerProps={bannerProps}
          />

          <div className="header-top-container">
            <div className="header-navigation">
              <div className="navigation-menu">
                <button name="parents" className="header-link" onClick={() => setOpenParents(!openParents)}>
                  Parents
                  <img
                    className={openParents ? 'flipped-arrow dropdown-arrow' : 'dropdown-arrow'}
                    src={`${imgURL}/global/icons/single_arrow.svg`}
                    alt="dropdown arrow"
                  />
                </button>
                {openParents ? (
                  <OutsideClickHandler onOutsideClick={toggleParents}>
                    <ul className="menu-dropdown">
                      <li className="menu-link">
                        <Link to="HowItWorks">How it Works</Link>
                      </li>
                      <li className="menu-link">
                        <a href="https://homerlearning-codespark.kustomer.help/?themeId=6298f569784e442bb588ae2a&status=published">
                          FAQ
                        </a>
                      </li>
                      <li className="menu-link">
                        <a href={`${process.env.REACT_APP_SIGNUP_BASE_URL}`}>Free Trial</a>
                      </li>
                      <li className="menu-link">
                        <a href={`${process.env.REACT_APP_ACCOUNTS_BASE_URL}/gift`}>Buy a Gift</a>
                      </li>
                      <li className="menu-link">
                        <a href={`${process.env.REACT_APP_ACCOUNTS_BASE_URL}/promo`}>Redeem a Gift</a>
                      </li>
                      <li className="menu-link">
                        <a href={`${process.env.REACT_APP_ACCOUNTS_BASE_URL}/login`}>Login</a>
                      </li>
                    </ul>
                  </OutsideClickHandler>
                ) : null}
              </div>
              <div className="navigation-menu">
                <button className="header-link" onClick={() => setOpenEducators(!openEducators)}>
                  Educators
                  <img
                    className={openEducators ? 'flipped-arrow dropdown-arrow' : 'dropdown-arrow'}
                    src={`${imgURL}/global/icons/single_arrow.svg`}
                    alt="dropdown arrow"
                  />
                </button>
                {openEducators ? (
                  <OutsideClickHandler onOutsideClick={toggleEducators}>
                    <ul className="menu-dropdown educators">
                      <li className="menu-link">
                        <Link to="Educators">Resources</Link>
                      </li>
                      <li className="menu-link">
                        <Link to="HourOfCode">Hour of Code</Link>
                      </li>
                      <li className="menu-link">
                        <a href={`${process.env.REACT_APP_DASHBOARD_BASE_URL}`}>Teacher Dashboard</a>
                      </li>
                      <li className="menu-link">
                        <a href="https://teach.codespark.com/courses/codespark-ignite">Professional Development</a>
                      </li>
                      <li className="menu-link">
                        <a href="/play">Play Online</a>
                      </li>
                    </ul>
                  </OutsideClickHandler>
                ) : null}
              </div>
              <div className="navigation-menu">
                <button className="header-link" onClick={() => setOpenAbout(!openAbout)}>
                  About
                  <img
                    className={openAbout ? 'flipped-arrow dropdown-arrow' : 'dropdown-arrow'}
                    src={`${imgURL}/global/icons/single_arrow.svg`}
                    alt="dropdown arrow"
                  />
                </button>
                {openAbout ? (
                  <OutsideClickHandler onOutsideClick={toggleAbout}>
                    <ul className="menu-dropdown about">
                      <li className="menu-link">
                        <Link to="About">About Us</Link>
                      </li>
                      <li className="menu-link">
                        <a
                          href={`${process.env.REACT_APP_BLOG_BASE_URL}/posts/codespark-academy-award-winning-coding-app`}
                        >
                          Awards
                        </a>
                      </li>
                      <li className="menu-link">
                        <Link to="Press">Press</Link>
                      </li>
                      <li className="menu-link">
                        <Link to="Contact">Contact Us</Link>
                      </li>
                    </ul>
                  </OutsideClickHandler>
                ) : null}
              </div>
            </div>
            <div className="logo">
              <Link to="Home">
                <img src={`${imgURL}/global/logo/codespark-by-begin.png`} alt="codeSpark" />
              </Link>
            </div>
            <div className="login-link">
              <a className="login-link-test" href={`${process.env.REACT_APP_ACCOUNTS_BASE_URL}/login`}>
                Login
              </a>
            </div>
            {props.location.pathname !== '/educators' && props.location.pathname !== '/hour-of-code' && (
              <div className="cta-button">
                <button
                  data-event-category="button"
                  data-event-action="main-menu"
                  data-event-label="7 day FREE Trial"
                  data-on="click"
                  onClick={(e) => goToAccounts(e, 1)}
                >
                  {ctaLabel}
                  <img className="basic-arrow" src={`${imgURL}/global/icons/basic_arrow_white.svg`} alt="arrow" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* MOBILE MENU */}
        <div className="mobile-menu-wrapper">
          <div className="grid-x grid-padding-x align-middle">
            <div className="logo small-6">
              <Link to="Home">
                <img src={`${imgURL}/global/logo/codespark-by-begin.png`} alt="codeSpark" />
              </Link>
            </div>
            <div className="hamburger-menu small-6">
              {openMobileMenu ? (
                <i className="fa fa-times" aria-hidden="true" onClick={() => setOpenMobileMenu(false)} />
              ) : (
                <i className="fa fa-bars" aria-hidden="true" onClick={() => setOpenMobileMenu(true)} />
              )}
            </div>
          </div>
          {openMobileMenu && (
            <div className="mobile-menu-open">
              <div className="mobile-navigation-menu parents">
                <Collapsible
                  trigger="Parents"
                  transitionTime={250}
                  easing="ease"
                  open={openParents}
                  handleTriggerClick={openParents}
                >
                  <ul className="menu-collapsible--menu-items">
                    <li className="menu-link">
                      <Link to="HowItWorks">How it Works</Link>
                    </li>
                    <li className="menu-link">
                      <a href="https://homerlearning-codespark.kustomer.help/?themeId=6298f569784e442bb588ae2a&status=published">
                        FAQ
                      </a>
                    </li>
                    <li className="menu-link">
                      <a href={`${process.env.REACT_APP_ACCOUNTS_BASE_URL}`}>Free Trial</a>
                    </li>
                    <li className="menu-link">
                      <a href={`${process.env.REACT_APP_ACCOUNTS_BASE_URL}/gift`}>Buy a Gift</a>
                    </li>
                    <li className="menu-link">
                      <a href={`${process.env.REACT_APP_ACCOUNTS_BASE_URL}/promo`}>Redeem a Gift</a>
                    </li>
                    <li className="menu-link">
                      <a href={`${process.env.REACT_APP_ACCOUNTS_BASE_URL}/login`}>Login</a>
                    </li>
                  </ul>
                </Collapsible>
              </div>
              <div className="mobile-navigation-menu">
                <Collapsible
                  trigger="Educators"
                  transitionTime={250}
                  easing="ease"
                  open={openEducators}
                  handleTriggerClick={openEducators}
                >
                  <ul className="menu-collapsible--menu-items educators">
                    <li className="menu-link">
                      <Link to="Educators">Resources</Link>
                    </li>
                    <li className="menu-link">
                      <Link to="HourOfCode">Hour of Code</Link>
                    </li>
                    <li className="menu-link">
                      <a href={`${process.env.REACT_APP_DASHBOARD_BASE_URL}`}>Teacher Dashboard</a>
                    </li>
                    <li className="menu-link">
                      <a href="/play">Play Online</a>
                    </li>
                  </ul>
                </Collapsible>
              </div>
              <div className="mobile-navigation-menu">
                <Collapsible
                  trigger="About"
                  transitionTime={250}
                  easing="ease"
                  open={openAbout}
                  handleTriggerClick={openAbout}
                >
                  <ul className="menu-collapsible--menu-items about">
                    <li className="menu-link">
                      <Link to="About">About Us</Link>
                    </li>
                    <li className="menu-link">
                      <a
                        href={`${process.env.REACT_APP_BLOG_BASE_URL}/posts/codespark-academy-award-winning-coding-app`}
                      >
                        Awards
                      </a>
                    </li>
                    <li className="menu-link">
                      <Link to="Press">Press</Link>
                    </li>
                    <li className="menu-link">
                      <Link to="Contact">Contact Us</Link>
                    </li>
                  </ul>
                </Collapsible>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/global/Header/index.js